import { Helmet } from 'react-helmet-async';
import { Box, Flex } from '@chakra-ui/react';
import { FormikHelpers } from 'formik';
import AssetManagerPage from 'components/common/AssetManagerPage';
import Layout from 'components/common/Layout';
import { useFetchCountries, useFetchSettings, useToast, useUpdateSettings } from 'hooks';
import { Alert, AlertDescription, AlertIcon, Spinner } from 'components/UI';
import { checkIfError } from 'utils/helpers';
import { ISettingsValues } from 'types';
import Container from 'components/common/Container';
import SettingsForm from 'components/Settings/SettingsForm/SettingsForm';
import Sidebar from 'components/common/Layout/Sidebar';
import { Navigate, useParams } from 'react-router-dom';
import { sections } from 'constants/settings';
import SidebarIcon from 'components/common/Layout/Sidebar/SidebarIcon';
import SidebarText from 'components/common/Layout/Sidebar/SidebarText';
import { Pages } from 'constants/pages';
import { useEffect } from 'react';

const Settings = () => {
  const [fetchSettings, { loading: fetchSettingsLoading, error: fetchSettingsError, data: settingsData }] =
    useFetchSettings();

  const [fetchCountries, { loading: fetchCountriesLoading, error: fetchCountriesError, data: countriesData }] =
    useFetchCountries();

  const { section } = useParams();

  const [showToast] = useToast();

  const [updateSettings, { error: updateSettingsError }] = useUpdateSettings();

  const handleLoadSettings = async () => {
    await Promise.all([fetchSettings(), fetchCountries()]);
  };

  const handleSaveSettings = async (
    { headerLogo, loginLogo, emailLogo, loginBackgroundImage, ...values }: ISettingsValues | Record<string, never>,
    { setSubmitting }: FormikHelpers<ISettingsValues | Record<string, never>>
  ) => {
    const newSettings = Object.entries({
      ...values,
      directory: 'settings',
    }).reduce((acc, [key, val]) => {
      acc.append(key, val as string);
      return acc;
    }, new FormData());

    if (headerLogo && typeof headerLogo !== 'string') {
      newSettings.append('headerLogo', headerLogo[0]);
    }

    if (loginLogo && typeof loginLogo !== 'string') {
      newSettings.append('loginLogo', loginLogo[0]);
    }

    if (emailLogo && typeof emailLogo !== 'string') {
      newSettings.append('emailLogo', emailLogo[0]);
    }

    newSettings.append(
      'loginBackgroundImage',
      loginBackgroundImage && typeof loginBackgroundImage !== 'string' ? loginBackgroundImage[0] : ''
    );

    await updateSettings(newSettings);

    setSubmitting(false);

    showToast({ message: 'Settings updated successfully.' });
  };

  useEffect(() => {
    handleLoadSettings();
  }, []);

  useEffect(() => {
    if (!updateSettingsError) return;
    showToast({ message: updateSettingsError, status: 'error' });
  }, [updateSettingsError]);

  if (checkIfError(settingsData) || checkIfError(countriesData))
    return (
      <Layout testId="settings-page" pages={[Pages.Settings]}>
        <Helmet title="Settings" />
        <AssetManagerPage title="Settings">
          <Container>
            <Flex justifyContent="center" p={4}>
              <Alert status="error" w="auto" borderRadius={4} variant="solid">
                <AlertIcon />
                <AlertDescription maxWidth="sm">
                  {fetchSettingsError ?? updateSettingsError ?? fetchCountriesError}
                </AlertDescription>
              </Alert>
            </Flex>
          </Container>
        </AssetManagerPage>
      </Layout>
    );

  const sidebarItem = sections.find(({ id }) => id === (section ?? 'general'));

  const overflowStyles = !sidebarItem?.datatable ? { overflow: 'hidden' } : {};

  if (!sidebarItem) {
    return <Navigate to="notfound" replace />;
  }

  const renderForm =
    fetchSettingsLoading || fetchCountriesLoading || !settingsData || !countriesData ? (
      <Flex justifyContent="center" alignItems="center">
        <Spinner size="lg" />
      </Flex>
    ) : (
      <Container h="full" justifyContent="stretch">
        <Flex gap={6} w="full" h="full">
          <Box h="full" flex="0 1 23%">
            <Sidebar
              h="full"
              contentProps={{ h: 'full' }}
              items={sections.map(({ icon: Icon, text, ...item }) => ({
                ...item,
                text: <SidebarText>{text}</SidebarText>,
                icon: (
                  <SidebarIcon>
                    <Icon />
                  </SidebarIcon>
                ),
              }))}
            />
          </Box>

          <SettingsForm
            countries={countriesData}
            section={sidebarItem}
            formComponent={sidebarItem.component}
            onSubmit={handleSaveSettings}
            initialValues={settingsData}
          />
        </Flex>
      </Container>
    );

  return (
    <Layout
      sx={{
        h: '100vh',
        w: '100vw',
        alignItems: 'stretch',
        display: 'flex',
        flex: 'flex-grow',
        minH: 915,
        ...overflowStyles,
      }}
      pageProps={overflowStyles}
      bodyProps={overflowStyles}
      testId="settings-page"
      pages={[Pages.Settings]}
    >
      <Helmet title="Settings" />
      <AssetManagerPage
        bodyProps={{ h: 'full', ...overflowStyles }}
        containerProps={overflowStyles}
        title="Settings"
        h="full"
        {...overflowStyles}
      >
        {renderForm}
      </AssetManagerPage>
    </Layout>
  );
};

export default Settings;
